export type RaceType =
  | 'sprint'
  | 'olympic'
  | 'middle_distance'
  | 'long_distance'
  | '5k'
  | '10k'
  | 'half_marathon'
  | 'marathon'
  | 'open_water_swim'
  | 'criterium'
  | 'road_race'
  | 'time_trial'
  | 'other'

export type DisciplineSplit = {
  discipline: 'swim' | 'bike' | 'run' | 'transition'
  time: number // in seconds
  distance?: number // in meters/km
  pace?: string // formatted pace string
  rank?: number
  percentile?: number
}

export interface RaceResult {
  id: string
  name: string
  raceType: string
  date: string // ISO date string
  location?: string

  // Overall results
  totalTime?: number // in seconds
  overallRank?: number
  overallPercentile?: number
  totalParticipants?: number

  // Age group results
  ageGroupRank?: number
  ageGroupPercentile?: number
  ageGroupParticipants?: number
  ageGroup?: string // e.g., "M25-29"

  // Discipline splits
  splits: DisciplineSplit[]

  // Additional info
  weather?: string
  temperature?: number // in Celsius
  notes?: string
  raceUrl?: string

  // Metadata
  userId: string
  status: 'completed' | 'upcoming' | 'dnf' | 'dns'
  createdAt: string
  updatedAt: string
}

export interface RaceResultsFilter {
  search?: string
  raceType?: string
  status?: string
  year?: string
  dateFrom?: string
  dateTo?: string
}

export interface RaceResultsTableProps {
  data: RaceResult[]
  loading?: boolean
  onEdit?: (raceResult: RaceResult) => void
  onDelete?: (id: string) => void
  onAdd?: () => void
  onView?: (raceResult: RaceResult) => void
}

export type RaceTypeConfig = {
  id: RaceType
  label: string
  description: string
  typical_distances: {
    swim?: string
    bike?: string
    run?: string
  }
  category: 'triathlon' | 'running' | 'cycling' | 'swimming' | 'other'
}

// Helper function to format time from seconds to readable format
export const formatTime = (seconds: number): string => {
  if (seconds === 0) return '—'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

// Helper function to format pace
export const formatPace = (timeSeconds: number, distanceKm: number): string => {
  if (timeSeconds === 0 || distanceKm === 0) return '—'

  const paceSecondsPerKm = timeSeconds / distanceKm
  const minutes = Math.floor(paceSecondsPerKm / 60)
  const seconds = Math.floor(paceSecondsPerKm % 60)

  return `${minutes}:${seconds.toString().padStart(2, '0')}/km`
}

// Race type configurations with labels and categories
export const RACE_TYPE_CONFIGS: RaceTypeConfig[] = [
  // Triathlon races
  {
    id: 'sprint',
    label: 'Sprint',
    description: 'Sprint distance triathlon',
    category: 'triathlon',
    typical_distances: { swim: '750m', bike: '20km', run: '5km' },
  },
  {
    id: 'olympic',
    label: 'Olympic',
    description: 'Olympic distance triathlon',
    category: 'triathlon',
    typical_distances: { swim: '1.5km', bike: '40km', run: '10km' },
  },
  {
    id: 'middle_distance',
    label: 'Half Ironman',
    description: 'Middle Distance (Ironman 70.3) triathlon',
    category: 'triathlon',
    typical_distances: { swim: '1.9km', bike: '90km', run: '21.1km' },
  },
  {
    id: 'long_distance',
    label: 'Ironman',
    description: 'Long Distance (Ironman) triathlon',
    category: 'triathlon',
    typical_distances: { swim: '3.8km', bike: '180km', run: '42.2km' },
  },

  // Running races
  { id: '5k', label: '5K', description: '5 kilometer run', category: 'running', typical_distances: { run: '5km' } },
  { id: '10k', label: '10K', description: '10 kilometer run', category: 'running', typical_distances: { run: '10km' } },
  {
    id: 'half_marathon',
    label: 'Half Marathon',
    description: '21.1 kilometer run',
    category: 'running',
    typical_distances: { run: '21.1km' },
  },
  {
    id: 'marathon',
    label: 'Marathon',
    description: '42.2 kilometer run',
    category: 'running',
    typical_distances: { run: '42.2km' },
  },

  // Swimming races
  {
    id: 'open_water_swim',
    label: 'Open Water Swim',
    description: 'Open water swimming event',
    category: 'swimming',
    typical_distances: { swim: 'varies' },
  },

  // Cycling races
  {
    id: 'criterium',
    label: 'Criterium',
    description: 'Short circuit cycling race',
    category: 'cycling',
    typical_distances: { bike: 'varies' },
  },
  {
    id: 'road_race',
    label: 'Road Race',
    description: 'Road cycling race',
    category: 'cycling',
    typical_distances: { bike: 'varies' },
  },
  {
    id: 'time_trial',
    label: 'Time Trial',
    description: 'Individual time trial',
    category: 'cycling',
    typical_distances: { bike: 'varies' },
  },

  // Other
  { id: 'other', label: 'Other', description: 'Other race type', category: 'other', typical_distances: {} },
]

// Helper functions for race types
export const getRaceTypeConfig = (raceType: string): RaceTypeConfig | undefined => {
  return RACE_TYPE_CONFIGS.find((config) => config.id === raceType)
}

export const getRaceTypesByCategory = (category: RaceTypeConfig['category']): RaceTypeConfig[] => {
  return RACE_TYPE_CONFIGS.filter((config) => config.category === category)
}

export const getAllRaceTypes = (): RaceTypeConfig[] => {
  return RACE_TYPE_CONFIGS
}
